import { makeAutoObservable } from 'mobx'
import { Call } from '@twilio/voice-sdk'
import { formatPhoneNumberToInternational } from 'shared/lib'
import { type ICallParticipantContact, type IResponseCallQueueParticipant } from 'entities/Call'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { CallPopUpDefaultStore } from 'widgets/CallPopUp/store/callPopUpDefaultStore'
import type {
  ICallPopUpDefaultView,
  ICallPopUpPowerDialerStoreV2InitData,
  ICallTeamParticipant,
} from 'widgets/CallPopUp'

export class CallTeam {
  id!: number
  name!: string
  number!: string
  position!: ICallTeamParticipant['position']
  private readonly _store: CallPopUpDefaultStore
  private _status: Call.State = Call.State.Closed

  constructor(response: ICallTeamParticipant, private _entities: IEntitiesStore) {
    this.id = response.id
    this.name = response.name
    this.number = response.number
    this.position = response.position || {
      x: 0,
      y: 0,
    }
    this._store = new CallPopUpDefaultStore(this._entities)

    makeAutoObservable(this)
  }

  get internationalNumber() {
    return formatPhoneNumberToInternational(this.number)
  }

  setView = (view: ICallPopUpDefaultView) => {
    this._store.setView(view)
  }

  setContactFrom = (inbox: Inbox) => {
    this._store.setContactFrom(inbox)
  }

  setContactTo = (contact: Contact) => {
    this._store.setContactTo(contact)
  }

  setStatusCall = (status: Call.State) => {
    this._store.setStatusCall(status)
  }

  setLocalStatusCall = (status: Call.State) => {
    this._status = status
  }

  setPowerDialerInitData = async (initData: ICallPopUpPowerDialerStoreV2InitData) => {
    await this._store.callPopUpPowerDialerStoreV2.setPowerDialerInitData(initData)
  }

  addItemQueue = (item: ICallParticipantContact) => {
    this._store.callPopUpQueueStore.addItem(item)
  }

  setItemQueue = (item: IResponseCallQueueParticipant | null) => {
    this._store.callPopUpQueueStore.setItem(item)
  }

  resetAll = () => {
    this._store.resetReactions()
    this._store.resetPopUp()
    this._store.resetCommon()
  }

  get status() {
    return this._status
  }

  get store() {
    return this._store
  }

  get view() {
    return this._store.view || 'default'
  }

  get hasItemQueue() {
    return this._store.callPopUpQueueStore.hasItem
  }

  get isPowerDialerRunning() {
    return this._store.callPopUpPowerDialerStoreV2.isPowerDialerRunning
  }

  get hasItemsQueue() {
    return this._store.callPopUpQueueStore.hasItems
  }

  get callPopUpCallInsightCardsCallTypeStore() {
    return this._store.callPopUpInsightCardsStore.callPopUpCallInsightCardsCallTypeStore
  }

  get isCallTypesModal() {
    return this.callPopUpCallInsightCardsCallTypeStore?.showModal
  }

  get callPopUpInsightCardsStore() {
    return this._store.callPopUpInsightCardsStore
  }
}
